import { Injectable } from '@angular/core';
import { QueryResultT } from '@core/types/query-result-t';
import { BudgetTypeEnum } from '@enums/BudgetType';
import { EstimateTypeEnum } from '@enums/EstimateType';
import { StatusEnum } from '@enums/Status';
import { StructureDepthEnum } from '@enums/StructureDepth';
import { AverageRateDto } from '@interfaces/AverageRateDto';
import { Category } from '@interfaces/Category';
import { CategoryDetail } from '@interfaces/CategoryDetail';
import { Classification } from '@interfaces/Classification';
import { ClassificationEstimate } from '@interfaces/ClassificationEstimate';
import { ClassificationLine } from '@interfaces/ClassificationLine';
import { Company } from '@interfaces/Company';
import { CustomerDto } from '@interfaces/CustomerDto';
import { Department } from '@interfaces/Department';
import { DepartmentDetail } from '@interfaces/DepartmentDetail';
import { DepartmentEstimate } from '@interfaces/DepartmentEstimate';
import { Estimate } from '@interfaces/Estimate';
import { EstimateGroup } from '@interfaces/EstimateGroup';
import { EstimateLine } from '@interfaces/EstimateLine';
import { EstimateRevisionDto } from '@interfaces/EstimateRevisionDto';
import { ExpenseBillingMethodDto } from '@interfaces/ExpenseBillingMethodDto';
import { ExpenseLine } from '@interfaces/ExpenseLine';
import { GroupCostTypeDto } from '@interfaces/GroupCostTypeDto';
import { ImportedProjectStructureDto } from '@interfaces/ImportedProjectStructureDto';
import { InternalFeeBillingMethodDto } from '@interfaces/InternalFeeBillingMethodDto';
import { InternalFeeLine } from '@interfaces/InternalFeeLine';
import { InternalResource } from '@interfaces/InternalResource';
import { InternalResource_Estimate } from '@interfaces/InternalResource_Estimate';
import { InternalResourceGroup } from '@interfaces/InternalResourceGroup';
import { LaborBillingMethodDto } from '@interfaces/LaborBillingMethodDto';
import { Level1 } from '@interfaces/Level1';
import { Level1Detail } from '@interfaces/Level1Detail';
import { Level2 } from '@interfaces/Level2';
import { Level2Detail } from '@interfaces/Level2Detail';
import { ProblematicSubLevelsDto } from '@interfaces/ProblematicSubLevelsDto';
import { Project } from '@interfaces/Project';
import { Resource } from '@interfaces/Resource';
import { ServiceTypeDto } from '@interfaces/ServiceTypeDto';
import { Structure } from '@interfaces/Structure';
import { UserDto } from '@interfaces/UserDto';
import { core, Entity, EntityQuery, FilterQueryOp, MergeStrategy, Predicate, ValidationError } from 'breeze-client';
import { isNil, pick } from 'lodash';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, EMPTY, from, Observable, of, ReplaySubject } from 'rxjs';
import { debounceTime, distinctUntilChanged, share } from 'rxjs/operators';
import { PopEntityStateEnum } from '../../models/enums/PopEntityState';
import { BreezeBaseService } from './breeze-base.service';
import { TotalsDataService } from './total.service';

@Injectable({
  providedIn: 'root'
})
export class EstimateService extends BreezeBaseService {
  private internalResources: InternalResource[];
  private classifications: Classification[] = [];
  private departments: Department[] = [];
  private savingStatus = 'Saved';
  private structures: Structure[] = [];

  private toolbarSubject$ = new ReplaySubject<void>(1);
  private dialogSubject$ = new ReplaySubject<void>(1);

  private isCurrentGroupApproved$ = new BehaviorSubject<boolean>(false);
  private alreadyDeployedSubLevels$ = new BehaviorSubject<ProblematicSubLevelsDto | undefined>(undefined);

  private currentEstimate$ = new ReplaySubject<Estimate>(1);

  private _currentEstimate: Estimate;
  private getResourcesPromise: Promise<Resource[]>;

  public get currentEstimate() {
    return this._currentEstimate;
  }
  private set currentEstimate(value: Estimate) {
    this._currentEstimate = value;
    this.currentEstimate$.next(value);
  }

  public observeCurrentEstimate() {
    return this.currentEstimate$.asObservable().pipe(debounceTime(100));
  }

  public static getTitle(currentEstimate: Estimate): string {
    const useProjectCode = !currentEstimate.projectName?.includes(currentEstimate.projectCode);

    const title = [
      currentEstimate.codeRef,
      useProjectCode ? currentEstimate.projectCode : undefined,
      currentEstimate.projectName
    ]
      .filter(str => !!str?.trim())
      .join(' - ');

    return title;
  }

  public constructor(
    private savingData: TotalsDataService,
    private logger: NGXLogger
  ) {
    super('estimate', 'estimate');

    this.savingData.currentSavingStatus.subscribe(savingStatus => (this.savingStatus = savingStatus));
  }

  public saveChanges() {
    return from(this.saveChangesAsync());
  }

  public async saveChangesAsync(throwError = false) {
    this.savingStatus = 'Saving';
    this.savingData.changeSavingStatus(this.savingStatus);
    if (!this.em.hasChanges()) {
      this.savingStatus = 'Saved';
      this.savingData.changeSavingStatus(this.savingStatus);
    }

    try {
      const saveResults = await this.em.saveChanges();
      this.treetableEmitChanges();
      this.savingStatus = 'Saved';
      this.savingData.changeSavingStatus(this.savingStatus);
      this.logger.info(`EstimateService.SaveChanges() completed ${saveResults.entities.length} entites`);
    } catch (error) {
      this.logger.info(`EstimateService.SaveChanges() failed: ${String(error)}`);
      this.savingStatus = 'Error';
      this.savingData.changeSavingStatus(this.savingStatus);

      // get and log Breeze Validation Errors (Client-Side)
      this.em.getEntities().forEach(entity => {
        const errors = entity.entityAspect.getValidationErrors();

        errors.forEach((ve: ValidationError) => {
          const errorMessage = ve.errorMessage;
          const property = String(ve.property);
          this.logger.warn(`${entity.entityType.toString()}: PROP:${property} message ${errorMessage}`);
          this.logger.info(ve);
        });
      });
      if (throwError) {
        throw error;
      }
    }
  }

  public dialogEmitChanges() {
    this.dialogSubject$.next();
  }

  public treetableEmitChanges() {
    this.toolbarSubject$.next();
  }

  public dialogHasChanges(): Observable<void> {
    return this.dialogSubject$.pipe(debounceTime(30));
  }

  public treetableHasChanges(): Observable<void> {
    return this.toolbarSubject$.pipe(share());
  }

  public refreshEstimate(entity: Estimate = null): Observable<Estimate> {
    if (entity === null) {
      entity = this.currentEstimate;
    }
    return from(this.refreshEstimateAsync(entity));
  }

  public async refreshEstimateAsync(entity: Estimate = null) {
    if (entity === null) {
      entity = this.currentEstimate;
    }

    const entityKey = entity.entityAspect.getKey();
    const query = EntityQuery.fromEntityKey(entityKey).using(MergeStrategy.OverwriteChanges);

    const response = await this.em.executeQuery(query);
    this.currentEstimate = response.results[0] as Estimate;
    return this.currentEstimate;
  }

  public submitEstimate(
    estimateId: string,
    totalHours: number,
    totalCostFees: number,
    totalBillableFees: number,
    totalCostExpenses: number,
    totalBillableExpenses: number,
    totalBillableFinal: number,
    hasCRM: boolean
  ): Promise<number[]> {
    const query = EntityQuery.from('SubmitEstimate').withParameters({
      estimateId: estimateId,
      totalHours: totalHours,
      totalCostFees: totalCostFees,
      totalBillableFees: totalBillableFees,
      totalCostExpenses: totalCostExpenses,
      totalBillableExpenses: totalBillableExpenses,
      totalBillableFinal: totalBillableFinal,
      hasCRM: hasCRM
    });

    return this.executeQuery<number>(query)
      .then(response => {
        return response.results;
      })
      .catch((error: Error) => {
        this.logger.error(`EstimateService.submitEstimate(estimateId = ${estimateId} ) failed`);
        throw error;
      });
  }

  public getEstimatedFeesCRM(codeRef: string): Observable<number> {
    if (!codeRef) return EMPTY;

    const query = EntityQuery.from('GetEstimatedFees').withParameters({ codeRef: codeRef });

    return from(
      this.executeQuery<number>(query)
        .then(response => {
          return response.results[0];
        })
        .catch((error: Error) => {
          this.logger.error(`EstimateService.getEstimatedFeesCRM(codeRef = ${codeRef}) : ${error.message}`);
          throw error;
        })
    );
  }

  public getTotalBillableFinal(estimateId: string): Observable<number> {
    if (!estimateId) return EMPTY;

    const query = EntityQuery.from('GetTotalBillableFinal').withParameters({ estimateId: estimateId });

    return from(
      this.executeQuery<number>(query)
        .then(response => {
          return response.results[0];
        })
        .catch((error: Error) => {
          this.logger.error(`EstimateService.getTotalBillableFinal(estimateId = ${estimateId}) : ${error.message}`);
          throw error;
        })
    );
  }

  /**
   * Returns project template structures that match the given code
   * @param projectCode
   */
  public async getProjectStructuresAsync(projectCode: string) {
    const query = EntityQuery.from('ProjectStructures').withParameters({ projectCode: projectCode });

    const result = await this.executeQuery(query);
    return result.results as ImportedProjectStructureDto[];
  }

  /**
   * Returns all average rates from Sifia
   * @param projectTemplateCode
   */
  public getClassDeptRates(companyCode: string): Observable<AverageRateDto[]> {
    return from(this.getClassDeptRatesAsync(companyCode));
  }

  /**
   * Returns all average rates from Sifia
   * @param projectTemplateCode
   */
  private getClassDeptRatesAsync(companyCode: string) {
    const query = EntityQuery.from('ClassDeptRates').withParameters({
      companyCode: companyCode
    });

    return this.em.executeQuery(query).then((response: QueryResultT<AverageRateDto>) => {
      return response.results;
    });
  }

  public getLaborBillingMethodCodes(): Observable<string[]> {
    const query = EntityQuery.from('LaborBillingMethodCodes');

    return from(
      this.executeQuery<string>(query)
        .then(response => response.results)
        .catch((error: Error) => {
          this.logger.error(`EstimateService.getLaborBillingMethodCodes() : ${error.message}`);
          throw error;
        })
    );
  }

  public getExpenseBillingMethodCodes(): Observable<string[]> {
    const query = EntityQuery.from('ExpenseBillingMethodCodes');

    return from(
      this.executeQuery<string>(query)
        .then(response => response.results)
        .catch((error: Error) => {
          this.logger.error(`EstimateService.getExpenseBillingMethodCodes() : ${error.message}`);
          throw error;
        })
    );
  }

  public getInternalFeeBillingMethodCodes(): Observable<string[]> {
    const query = EntityQuery.from('InternalFeeBillingMethodCodes');

    return from(
      this.executeQuery<string>(query)
        .then(response => response.results?.map(r => r.trim()))
        .catch((error: Error) => {
          this.logger.error(`EstimateService.getInternalFeeBillingMethodCodes() : ${error.message}`);
          throw error;
        })
    );
  }

  public getExpenseBillingMethods(expenseBillingMethodeCode: string): Observable<ExpenseBillingMethodDto[]> {
    const query = EntityQuery.from('ExpenseBillingMethods').withParameters({
      expenseBillingMethodeCode
    });

    return from(
      this.executeQuery<ExpenseBillingMethodDto>(query)
        .then(response => response.results)
        .catch((error: Error) => {
          this.logger.error(`EstimateService.getExpenseBillingMethods() : ${error.message}`);
          throw error;
        })
    );
  }

  public getInternalFeeBillingMethods(internalFeeBillingMethodCode: string): Observable<InternalFeeBillingMethodDto[]> {
    const query = EntityQuery.from('InternalFeeBillingMethods').withParameters({
      internalFeeBillingMethodCode
    });

    return from(
      this.executeQueryResults<InternalFeeBillingMethodDto>(query).catch((error: Error) => {
        this.logger.error(
          `EstimateService.getInternalFeeBillingMethods(internalFeeBillingMethodCode = '${internalFeeBillingMethodCode}') : ${error.message}`
        );
        throw error;
      })
    );
  }

  public getLaborBillingMethods(laborBillingMethodCode: string): Observable<LaborBillingMethodDto[]> {
    const query = EntityQuery.from('LaborBillingMethods').withParameters({
      laborBillingMethodCode: laborBillingMethodCode
    });

    return from(
      this.executeQueryResults<LaborBillingMethodDto>(query).catch((error: Error) => {
        this.logger.error(
          `EstimateService.getLaborBillingMethods(laborBillingMethodCode = '${laborBillingMethodCode}') : ${error.message}`
        );
        throw error;
      })
    );
  }

  public getGroupCostTypes(): Observable<GroupCostTypeDto[]> {
    const query = EntityQuery.from('GroupCostTypes');

    return from(
      this.executeQueryResults<GroupCostTypeDto>(query).catch((error: Error) => {
        this.logger.error(`EstimateService.getGroupCostTypes() : ${error.message}`);
        return [];
      })
    );
  }

  /**
   * Returns cost items that match the given group category code
   * @param categoryGroupCode
   */
  public getCategoriesByGroupCode(categoryGroupCode: string): Observable<Category[]> {
    const query = EntityQuery.from('CategoriesByGroup').withParameters({ categoryGroupCode: categoryGroupCode });

    return from(
      this.executeQueryResults<Category>(query).catch((error: Error) => {
        this.logger.error(
          `EstimateService.getCategoriesByGroupCode(${categoryGroupCode} = ${categoryGroupCode}) : ${error.message}`
        );
        throw error;
      })
    );
  }

  public getEstimateRevisionDtos(estimateGroupId: string): Observable<EstimateRevisionDto[]> {
    if (!estimateGroupId) return EMPTY;

    const query = EntityQuery.from('GetEstimateRevisionDtos')
      .withParameters({ estimateGroupId: estimateGroupId })
      .orderBy('revision');

    return from(
      this.executeQueryResults<EstimateRevisionDto>(query).catch((error: Error) => {
        this.logger.error(
          `EstimateService.getRevisionsByGroupId(estimateGroupId = ${estimateGroupId}) : ${error.message}`
        );
        throw error;
      })
    );
  }

  /**
   * Returns all departments
   * */
  public getDepartments(user: UserDto, restrictedCompanies: string[]): Observable<Department[]> {
    return from(this.getDepartmentsAsync(user, restrictedCompanies));
  }

  public async getDepartmentsAsync(user: UserDto, restrictedCompanies: string[], getAll = false) {
    let query = EntityQuery.from('Departments').orderBy('code');

    if (!getAll) {
      query = query.where('isActive', '==', true);
    }

    const isAppAdmin = user.roles.filter(r => r.name === 'app-admin').length > 0;
    if (!isAppAdmin) {
      if (user.isRestrictedByCompany) {
        query = query.where('company', '==', user.company);
      } else if (restrictedCompanies.length > 0) {
        const predicates = [] as Predicate[];

        for (const rc of restrictedCompanies) {
          const predicate = Predicate.create('company', '!=', rc);
          predicates.push(predicate);
        }

        const combinedPredicates = Predicate.or(predicates);

        query = query.where(combinedPredicates);
      }
    }

    if (this.isQueryCached(query)) {
      return this.em.executeQueryLocally(query) as Department[];
    }

    return await this.executeQuery(query).then((response: QueryResultT<Department>) => {
      this.addLocalQuery(query);
      this.departments = response.results;
      return this.departments;
    });
  }

  public async getProjectTemplatesAsync() {
    const templates = await this.getProjectsAsync();
    return templates.filter(t => t.code?.startsWith('Z'));
  }

  public getProjects(): Observable<Project[]> {
    return from(this.getProjectsAsync());
  }

  public async getProjectsAsync() {
    const query = EntityQuery.from('Projects').orderBy('code');
    if (this.isQueryCached(query)) {
      return this.em.executeQueryLocally(query) as Project[];
    }

    return await this.executeQuery(query).then((response: QueryResultT<Project>) => {
      this.addLocalQuery(query);

      return response.results;
    });
  }

  /**
   *
   * @param projectCode
   */
  public getProjectById(projectCode: string): Observable<Project | undefined> {
    if (!projectCode) return EMPTY;

    const query = EntityQuery.from('Projects').where('code', '==', projectCode);

    return from(
      this.executeQuery(query)
        .then((response: QueryResultT<Project>) => {
          const projects = response.results;
          if (projects.length > 0) {
            return projects[0];
          }
          return undefined;
        })
        .catch((error: Error) => {
          this.logger.error(error);
          throw error;
        })
    );
  }

  /**
   * Returns all resources
   * */
  public getResources(user: UserDto, restrictedCompanies: string[]) {
    const isAlreadyRunning = this.getResourcesPromise != undefined;
    if (isAlreadyRunning) {
      return this.getResourcesPromise;
    }

    let query = EntityQuery.from('Resources').where('isActive', '==', true).orderBy('name');

    const isAppAdmin = user.roles.filter(r => r.name === 'app-admin').length > 0;
    if (!isAppAdmin) {
      query = EntityQuery.from('Resources').orderBy('name');
      if (user.isRestrictedByCompany) {
        query = EntityQuery.from('Resources')
          .where('isActive', '==', true)
          .where('company', '==', user.company)
          .orderBy('name');
      } else if (restrictedCompanies.length > 0) {
        const predicates = [] as Predicate[];

        for (const rc of restrictedCompanies) {
          const predicate = Predicate.create('company', '!=', rc);
          predicates.push(predicate);
        }

        const combinedPredicates = Predicate.or(predicates);

        query = EntityQuery.from('Resources').where('isActive', '==', true).where(combinedPredicates).orderBy('name');
      }
    }

    const promise = async () => {
      if (this.isQueryCached(query)) {
        return this.em.executeQueryLocally(query) as Resource[];
      }

      const resources = await this.executeQueryResults<Resource>(query);
      this.addLocalQuery(query);
      this.getResourcesPromise = undefined;
      return resources;
    };

    this.getResourcesPromise = promise();
    return this.getResourcesPromise;
  }

  /**
   * Returns all classes
   * */
  public getClassifications(isFilteredBySettings = false): Observable<Classification[]> {
    const query = EntityQuery.from('Classifications')
      .withParameters({ isFilteredBySettings })
      .where('isActive', '==', true)
      .orderBy('code');

    if (this.isQueryCached(query)) {
      return of(this.em.executeQueryLocally(query) as Classification[]);
    }

    return from(
      this.executeQueryResults<Classification>(query)
        .then(results => {
          if (results) {
            this.addLocalQuery(query);
            this.classifications = results;
            return this.classifications;
          }
        })
        .catch((error: Error) => {
          this.logger.error(`EstimateService.getClassifications() : ${error.message}`);
          throw error;
        })
    );
  }

  /**
   * Returns all Internal Resource Groups
   * */
  public async getInternalResourceGroups(): Promise<InternalResourceGroup[]> {
    const query = EntityQuery.from('InternalResourceGroups');

    if (this.isQueryCached(query)) {
      return this.em.executeQueryLocally(query) as InternalResourceGroup[];
    }

    return await this.em
      .executeQuery(query)
      .then((response: QueryResultT<InternalResourceGroup>) => {
        if (response.results) {
          this.addLocalQuery(query);
          return response.results;
        }
      })
      .catch((error: Error) => {
        this.logger.error(`EstimateService.getInternalResourceGroups() : ${error.message}`);
        throw error;
      });
  }

  /**
   * Returns all Internal Resources
   * */
  public getInternalResources(): Observable<InternalResource[]> {
    const query = EntityQuery.from('InternalResources').where('isActive', '==', true);

    if (this.isQueryCached(query)) {
      return of(this.em.executeQueryLocally(query) as InternalResource[]);
    }

    return from(
      this.executeQueryResults<InternalResource>(query)
        .then(results => {
          if (results) {
            this.addLocalQuery(query);
            this.internalResources = results;
            return this.internalResources;
          }
        })
        .catch((error: Error) => {
          this.logger.error(`EstimateService.getInternalResources() : ${error.message}`);
          throw error;
        })
    );
  }

  /**
   *
   * @param estimateId
   */
  public getStructuresByEstimateId(estimateId: string): Observable<Structure[]> {
    if (!estimateId) return EMPTY;

    const query = EntityQuery.from('Structures')
      .where('estimateId', '==', estimateId)
      .orderBy('level1.code,level2.code');

    return from(
      this.executeQueryResults<Structure>(query)
        .then(results => {
          this.structures = results;
          return this.structures;
        })
        .catch((error: Error) => {
          this.logger.error(error);
          throw error;
        })
    );
  }

  /**
   * Returns the estimate corresponding to the given id
   * @param code
   * @param revision
   */
  public getEstimateById(id: string): Observable<Estimate> {
    if (!id) return EMPTY;

    const query = EntityQuery.from('Estimates').where('id', '==', id);

    return from(
      this.executeQueryResult<Estimate>(query)
        .then(estimate => {
          this.currentEstimate = estimate;
          return this.currentEstimate;
        })
        .catch((error: Error) => {
          this.logger.error(`EstimateService.getEstimateById(id = ${id})' : ${error.message}`);
          throw error;
        })
    );
  }

  /**
   * Checks if at least one estimate was approved under this projectCode.
   * */
  public async hasApprovedNewEstimate(projectCode: string) {
    const keyStatus: keyof Estimate = 'status';
    const keyEstimateType: keyof Estimate = 'estimateType';
    const keyProjectCode: keyof Estimate = 'projectCode';

    const query = EntityQuery.from('Estimates')
      .where(keyStatus, FilterQueryOp.Equals, StatusEnum.Approved)
      .where(keyProjectCode, FilterQueryOp.Equals, projectCode)
      .where(keyEstimateType, FilterQueryOp.Equals, EstimateTypeEnum.New)
      .take(1);

    const estimates = await this.executeQueryResults<Estimate>(query);
    return estimates.length >= 1;
  }

  public async getEstimatePOPStatus(estimateId: string): Promise<PopEntityStateEnum> {
    const query = EntityQuery.from('Estimates').where('id', '==', estimateId).select('popEntityState');

    try {
      const estimate = await this.executeQueryResult<Estimate>(query);
      return estimate.popEntityState;
    } catch (error) {
      this.logger.error(`EstimateService.getEstimatePOPStatus(id = ${estimateId}) : ${String(error)}`);
      throw error;
    }
  }

  /**
   *
   * @param estimateId
   * @param level1Id
   * @param level2Id
   */
  public findLevel1Structures(estimateId: string, level1Id: string): Observable<Structure[]> {
    const query = EntityQuery.from('Structures')
      .where('estimateId', '==', estimateId)
      .where('level1Id', '==', level1Id);

    return from(
      this.executeQueryResults<Structure>(query).catch((error: Error) => {
        this.logger.error(
          `EstimateService.findLevel1Structures(estimateId = '${estimateId}', level1Id = '${level1Id}') : ${error.message}`
        );
        throw error;
      })
    );
  }

  /**
   *
   * @param estimateId
   * @param level1Id
   * @param level2Id
   */
  public findLevel2Structure(estimateId: string, level1Id: string, level2Id?: string): Observable<Structure> {
    const query = EntityQuery.from('Structures')
      .where('estimateId', '==', estimateId)
      .where('level1Id', '==', level1Id)
      .where('level2Id', '==', level2Id);

    return from(
      this.executeQueryResult<Structure>(query).catch((error: Error) => {
        this.logger.error(
          `EstimateService.findLevel1Structures(estimateId = '${estimateId}', level1Id = '${level1Id}', level2 = '${level2Id}') : ${error.message}`
        );
        throw error;
      })
    );
  }

  /**
   *
   * @param estimateId
   * @param departmentId
   */
  public findDepartmentEstimate(estimateId: string, departmentId: string): Observable<DepartmentEstimate> {
    const query = EntityQuery.from('DepartmentEstimates')
      .where('estimateId', '==', estimateId)
      .where('departmentId', '==', departmentId);

    return from(
      this.executeQueryResult<DepartmentEstimate>(query).catch((error: Error) => {
        this.logger.error(
          `EstimateService.findDepartmentEstimate(estimateId = '${estimateId}', departmentId = '${departmentId}') : ${error.message}`
        );
        throw error;
      })
    );
  }

  /**
   *
   * @param structureId
   * @param departmentEstimateId
   */
  public getEstimateLinesByStructByDeptEstim(
    structureId: string,
    departmentEstimateId: string
  ): Observable<EstimateLine[]> {
    const query = EntityQuery.from('EstimateLines')
      .where('structureId', '==', structureId)
      .where('departmentEstimateId', '==', departmentEstimateId);

    return from(
      this.executeQueryResults<EstimateLine>(query).catch((error: Error) => {
        this.logger.error(
          `EstimateService.findEstimateLine(structureId = '${structureId}', departmentEstimateId = '${departmentEstimateId}') : ${error.message}`
        );
        throw error;
      })
    );
  }

  public getLatestEstimates(user: UserDto, restrictedCompanies: string[]) {
    let query = EntityQuery.from('GetLatestEstimates');

    const isAppAdmin = user.roles.filter(r => r.name === 'app-admin').length > 0;
    if (!isAppAdmin) {
      if (user.isRestrictedByCompany) {
        query = EntityQuery.from('GetLatestEstimates').where('companyCode', '==', user.company);
      } else if (restrictedCompanies.length > 0) {
        const predicates = [] as Predicate[];

        for (const rc of restrictedCompanies) {
          const predicate = Predicate.create('companyCode', '!=', rc);
          predicates.push(predicate);
        }

        const combinedPredicates = Predicate.or(predicates);

        query = EntityQuery.from('GetLatestEstimates').where(combinedPredicates);
      }
    }

    return from(
      this.executeQueryResults<Estimate>(query).catch((error: Error) => {
        this.logger.error(`EstimateService.getLatestEstimates() : ${error.message}`);
        throw error;
      })
    );
  }

  public observeIsCurrentEstimateGroupApproved() {
    return this.isCurrentGroupApproved$.asObservable().pipe(distinctUntilChanged());
  }
  public async isCurrentEstimateGroupApproved() {
    const estimateId = this.currentEstimate.id;
    const query = EntityQuery.from('IsEstimateGroupApproved').withParameters({ estimateId: estimateId });

    const results = await this.executeQueryResult<boolean>(query);
    this.isCurrentGroupApproved$.next(results);
  }

  public createClassificationLine(
    estimateLineId: string,
    classificationEstimateId: string,
    hours = 0,
    averageRateCost = 0,
    averageRateBillable = 0
  ) {
    const initialValues = {
      id: core.getUuid(),
      hours: hours,
      rateCost: averageRateCost,
      rateBillable: averageRateBillable,
      estimateLineId: estimateLineId,
      classificationEstimateId: classificationEstimateId
    };

    const newClassificationLine = this.em.createEntity('ClassificationLine', initialValues);

    this.logger.info('ClassificationLine created ' + initialValues.id);

    return newClassificationLine;
  }

  public createInternalFeeLine(
    estimateLineId: string,
    internalResourceEstimateId: string,
    quantity = 0,
    rateCost = 0,
    rateBillable = 0
  ) {
    const initialValues: Partial<InternalFeeLine> = {
      id: core.getUuid(),
      quantity,
      rateCost,
      rateBillable,
      estimateLineId,
      internalResourceEstimateId
    };
    const newInternalFeeLine = this.em.createEntity('InternalFeeLine', initialValues) as InternalFeeLine;

    this.logger.info('InternalFeeLine created ' + initialValues.id);
    return newInternalFeeLine;
  }

  public createExpenseLine(estimateLineId: string, expenseLine: Partial<ExpenseLine>) {
    const initialValues: Partial<ExpenseLine> = {
      //defaults
      amountBillable: expenseLine?.amountBillable || 0,
      amountCost: expenseLine?.amountCost || 0,
      quantity: expenseLine?.quantity || 0,
      unitCost: expenseLine?.unitCost || 0,
      billableFinal: expenseLine?.billableFinal,
      pcExpenseId: expenseLine?.pcExpenseId,
      // overrides
      estimateLineId: estimateLineId
    };

    const newExpenseLine = this.em.createEntity('ExpenseLine', initialValues) as ExpenseLine;

    this.logger.info('ExpenseLine created ' + estimateLineId);

    return newExpenseLine;
  }

  public createDepartmentDetail(estimateLineId: string): DepartmentDetail {
    const newDepartmentDetail = this.em.createEntity('DepartmentDetail', { estimateLineId: estimateLineId });

    this.logger.info('DepartmentDetail created ' + estimateLineId);

    return newDepartmentDetail as DepartmentDetail;
  }

  public createCategoryDetail(estimateLineId: string): CategoryDetail {
    const newCategoryDetail = this.em.createEntity('CategoryDetail', { estimateLineId: estimateLineId });

    this.logger.info('CategoryDetail created ' + estimateLineId);

    return newCategoryDetail as CategoryDetail;
  }

  public createLevel1Detail(structureId: string): Level1Detail {
    const newLevel1Detail = this.em.createEntity('Level1Detail', { structureId: structureId });

    this.logger.info('Level1Detail created ' + structureId);

    return newLevel1Detail as Level1Detail;
  }

  public createLevel1(options: Partial<Level1>): Level1 {
    const id = core.getUuid();

    const level1: Partial<Level1> = {
      ...options,
      id: id,
      isNew: true
    };

    const newLevel1 = this.em.createEntity('Level1', level1) as Level1;

    this.logger.info('Level1 created ' + id);

    return newLevel1;
  }
  public createLevel2(options: Partial<Level2>): Level2 {
    const id = core.getUuid();

    const level2: Partial<Level2> = {
      id: id,
      isNew: true,
      ...options
    };

    const newLevel2 = this.em.createEntity('Level2', level2) as Level2;

    this.logger.info('Level2 created ' + id);
    return newLevel2;
  }

  public createLevel2Detail(structureId: string): Level2Detail {
    const newLevel2Detail = this.em.createEntity('Level2Detail', { structureId: structureId });

    this.logger.info('Level2Detail created ' + structureId);

    return newLevel2Detail as Level2Detail;
  }

  public addStructure(
    level1Id: string,
    level2Id: string | null = null,
    categoryGroupId: string = null,
    p: ImportedProjectStructureDto,
    level1IsUsingLaborBillingMethod = false,
    level2IsUsingLaborBillingMethod = false
  ) {
    const structure: Partial<Structure> = {
      id: core.getUuid(),

      level1Id: level1Id,
      level2Id: level2Id,
      categoryGroupId,
      estimateId: this.currentEstimate.id,
      level1IsLumpSum: p.level1IsLumpSum,
      level2IsLumpSum: p.level2IsLumpSum,
      level1IsMaxBudgeted: p.level1IsMaxBudgeted,
      level2IsMaxBudgeted: p.level2IsMaxBudgeted,
      level1IsUsingLaborBillingMethod,
      level2IsUsingLaborBillingMethod
    };

    const newStructure = this.em.createEntity('Structure', structure);

    this.logger.info('Structure created ' + structure.id);

    return newStructure as Structure;
  }

  public createStructure(
    estimateId: string,
    level1Id: string,
    level2Id = '',
    catGroupId = '',
    level1IsMaxBudgeted = false,
    level2IsMaxBudgeted = false,
    level1IsUsingLaborBillingMethod = false,
    level2IsUsingLaborBillingMethod = false,
    level1IsLumpSum = false,
    level2IsLumpSum = false
  ): Structure {
    const initialValues = {
      id: core.getUuid(),
      estimateId: estimateId,
      level1Id: level1Id,
      level2Id: level2Id || null,
      categoryGroupId: catGroupId ? catGroupId : null,
      level1IsLumpSum: level1IsLumpSum,
      level2IsLumpSum: level2IsLumpSum,
      level1IsMaxBudgeted,
      level2IsMaxBudgeted,
      level1IsUsingLaborBillingMethod,
      level2IsUsingLaborBillingMethod
    };

    const newStructure = this.em.createEntity('Structure', initialValues);

    this.logger.info('Structure created ' + initialValues.id);

    return newStructure as Structure;
  }

  /**
   * Create a new department-estimate with given parameters
   * @param estimateId
   * @param department
   * @param responsible
   */
  public createDepartmentEstimate(
    estimateId: string,
    departmentId: string,
    isAssociatedToAllStructures: boolean,
    responsible?: Resource
  ): DepartmentEstimate {
    const initialValues: Partial<DepartmentEstimate> = {
      id: core.getUuid(),
      estimateId: estimateId,
      departmentId: departmentId,
      isAssociatedToAllStructures: isAssociatedToAllStructures,
      responsibleId: responsible ? responsible.id : null
    };

    const newDepartmentEstimate = this.em.createEntity('DepartmentEstimate', initialValues);

    this.logger.info('DepartmentEstimate created ' + initialValues.id);

    return newDepartmentEstimate as DepartmentEstimate;
  }

  /**
   * Create a new classification-estimate with given parameters
   * @param estimateId
   * @param classification
   */
  public createClassificationEstimate(estimateId: string, classification: Classification): ClassificationEstimate {
    const initialValues = {
      id: core.getUuid(),
      rateCost: classification.rateCost,
      rateBillable: classification.rateBillable,
      estimateId: estimateId,
      classificationId: classification.id
    };

    const newClassificationEstimate = this.em.createEntity('ClassificationEstimate', initialValues);

    this.logger.info('ClassificationEstimate created ' + initialValues.id);

    return newClassificationEstimate as ClassificationEstimate;
  }

  public createInternalResourceEstimate(estimateId: string, internalResource: InternalResource): Entity {
    const initialValues = {
      id: core.getUuid(),
      internalResourceId: internalResource.id,
      estimateId: estimateId
    };

    const newClassificationEstimate = this.em.createEntity('InternalResource_Estimate', initialValues);

    this.logger.info('InternalResource_Estimate created ' + initialValues.id);

    return newClassificationEstimate;
  }

  public createEstimateLine(structureId: string, departmentEstimateId: string, categoryId?: string) {
    const initialValues = {
      id: core.getUuid(),
      structureId: structureId,
      departmentEstimateId: departmentEstimateId,
      categoryId: categoryId
    };

    const newEstimateLine = this.em.createEntity('EstimateLine', initialValues);

    this.logger.info('EstimateLine created ' + initialValues.id);

    return newEstimateLine as EstimateLine;
  }

  /**
   * Create a new estimate with given parameters
   * @param code
   * @param revision
   * @param title
   */
  public createEstimate(requestedValues: Partial<Estimate>, isNewGroup = true): Entity {
    let newEstimateGroup: EstimateGroup = <EstimateGroup>{};

    if (isNewGroup) {
      newEstimateGroup = this.createEstimateGroup();
    }

    const defaultValues: Partial<Estimate> = {
      revision: 0,
      status: StatusEnum.New,
      budgetType: BudgetTypeEnum.Summary,
      budgetTypeTime: BudgetTypeEnum.Summary,
      budgetTypeExpense: BudgetTypeEnum.Summary,
      budgetTypeInternalFee: BudgetTypeEnum.Summary,
      structureDepth: StructureDepthEnum.Level2,
      estimateGroupId: newEstimateGroup?.id ?? null,
      estimateType: EstimateTypeEnum.New
    };
    const newEstimate: Partial<Estimate> = {
      id: core.getUuid()
    };

    Object.assign(newEstimate, defaultValues);
    Object.assign(newEstimate, requestedValues);

    const createdEstimate = this.em.createEntity('Estimate', newEstimate) as Estimate;
    this.currentEstimate = createdEstimate;

    this.logger.info('Estimate created ' + createdEstimate.id);

    return createdEstimate;
  }

  /**
   * Create a new estimate group
   * */
  private createEstimateGroup(): EstimateGroup {
    const initialValues = {
      id: core.getUuid()
    };

    const newEstimateGroup = this.em.createEntity('EstimateGroup', initialValues) as EstimateGroup;

    this.logger.info('EstimateGroup created ' + initialValues.id);

    return newEstimateGroup;
  }

  public createDefaultClassification(language: string): Entity {
    const initialValues = {
      id: core.getUuid(),
      code: language == 'fr' ? '#TOUS' : '#ALL',
      description: language == 'fr' ? 'Tous' : 'All',
      rateCost: 0,
      rateBillable: 0,
      isActive: true
    };

    const newClassification = this.em.createEntity('Classification', initialValues);

    this.logger.info('Default Classification created ' + initialValues.id);

    return newClassification;
  }

  public addRevision(estimate: Estimate, maxRevisionValue: number): Estimate {
    if (!estimate) return;
    //create new Estimate and assign given Estimate values
    const modelEstimate = pick<Estimate, keyof Estimate>(estimate, [
      'codeRef',
      'projectCode',
      'projectName',
      'projectTypeId',
      'projectModelCode',
      'budgetType',
      'estimateType',
      'maxBudgeted',
      'laborBillingMethodCode',
      'expenseBillingMethodCode',
      'internalFeeBillingMethodCode',
      'projectManagerId',
      'clientName',
      'clientCode',
      'companyCode',
      'clientContactName',
      'rate',
      'dateStart',
      'dateEnd',
      'currencyCode',
      'currencyRate',
      'currencyRateOperation',
      'notes',
      'departmentCode',
      'companyCode',
      'estimateType',
      'useLaborBillingMethod',
      'hasPCSyncBeenRequested',
      'popEntityState',
      'internalResourceGroupId',
      'limitInternalResourcesByBillingMethod',
      'estimateGroupId',
      'adminId'
    ]);
    const newEstimate = this.createEstimate(modelEstimate, false) as Estimate;

    newEstimate.revision = maxRevisionValue + 1;
    newEstimate.budgetTypeExpense = estimate.budgetTypeExpense;
    newEstimate.budgetTypeInternalFee = estimate.budgetTypeInternalFee;
    newEstimate.budgetTypeTime = estimate.budgetTypeTime;
    newEstimate.departmentCode = estimate.departmentCode;
    newEstimate.estimateGroupId = estimate.estimateGroupId;
    newEstimate.pcProjectId = estimate.pcProjectId;
    newEstimate.status = StatusEnum.New;
    newEstimate.structureDepth = estimate.structureDepth;

    // copy Level1s, Level2s, Structures, Level1Details, Level2Details
    const level1Map = new Map<string, string>();
    const level2Map = new Map<string, string>();
    const structureMap = new Map<string, string>();

    for (const structure of estimate.structures) {
      let newLevel1Id = '';
      let newLevel2Id = '';

      // copy level1s
      if (!level1Map.has(structure.level1Id)) {
        const newLevel1 = this.createLevel1({
          code: structure.level1.code,

          description: structure.level1.description,
          level1ModelCode: structure.level1.level1ModelCode,
          duplicateSourceCode: structure.level1.duplicateSourceCode,
          isNew: structure.level1.isNew
        });
        newLevel1Id = newLevel1.id;
        level1Map.set(structure.level1Id, newLevel1Id);
      } else {
        newLevel1Id = level1Map.get(structure.level1Id);
      }

      // copy level2s
      if (structure.level2) {
        if (!level2Map.has(structure.level2Id)) {
          const newLevel2 = this.createLevel2({
            code: structure.level2.code,
            description: structure.level2.description,
            level2ModelCode: structure.level2.level2ModelCode,
            duplicateSourceCode: structure.level2.duplicateSourceCode,
            duplicateParentSourceCode: structure.level2.duplicateParentSourceCode,
            isNew: structure.level2.isNew
          });

          newLevel2Id = newLevel2.id;
          level2Map.set(structure.level2Id, newLevel2Id);
        } else {
          newLevel2Id = level2Map.get(structure.level2Id);
        }
      }

      // copy structures
      const newStructure = this.createStructure(
        newEstimate.id,
        newLevel1Id,
        newLevel2Id,
        structure.categoryGroupId,
        structure.level1IsMaxBudgeted,
        structure.level2IsMaxBudgeted,
        structure.level1IsUsingLaborBillingMethod,
        structure.level2IsUsingLaborBillingMethod,
        structure.level1IsLumpSum,
        structure.level2IsLumpSum
      );
      newStructure.pcLevel1ActivityId = structure.pcLevel1ActivityId;
      newStructure.pcLevel2ActivityId = structure.pcLevel2ActivityId;
      newStructure.laborBillingMethodCode = structure.laborBillingMethodCode;
      structureMap.set(structure.id, newStructure.id);

      // copy level1Details
      if (structure.level1Detail) {
        const newLevel1Detail = this.createLevel1Detail(newStructure.id);
        newLevel1Detail.dateEnd = structure.level1Detail.dateEnd;
        newLevel1Detail.dateStart = structure.level1Detail.dateStart;
        newLevel1Detail.notes = structure.level1Detail.notes;
      }

      // copy level2Details
      if (structure.level2Detail) {
        const newLevel2Detail = this.createLevel2Detail(newStructure.id);
        newLevel2Detail.dateEnd = structure.level2Detail.dateEnd;
        newLevel2Detail.dateStart = structure.level2Detail.dateStart;
        newLevel2Detail.notes = structure.level2Detail.notes;
      }
    }

    const departmentEstimateMap = new Map<string, string>();
    const classificationEstimateMap = new Map<string, string>();
    const internalResourceEstimateMap = new Map<string, string>();

    // copy departmentEstimates
    for (const deptEstim of estimate.departmentEstimates) {
      const newDepartmentEstimate = this.createDepartmentEstimate(
        newEstimate.id,
        deptEstim.department.id,
        deptEstim.isAssociatedToAllStructures,
        deptEstim.responsible
      );
      departmentEstimateMap.set(deptEstim.id, newDepartmentEstimate.id);
    }

    // copy classificationEstimates
    for (const classEstim of estimate.classificationEstimates) {
      const newClassificationEstimate = this.createClassificationEstimate(newEstimate.id, classEstim.classification);
      classificationEstimateMap.set(classEstim.id, newClassificationEstimate.id);
    }

    // copy internalResourceEstimates
    for (const resourceEstimate of estimate.internalResourceEstimates) {
      const newResourceEstimate = this.createInternalResourceEstimate(
        newEstimate.id,
        resourceEstimate.internalResource
      ) as InternalResource_Estimate;
      internalResourceEstimateMap.set(resourceEstimate.id, newResourceEstimate.id);
    }

    // copy estimateLines
    for (const structure of estimate.structures) {
      for (const estimateLine of structure.estimateLines) {
        const newStructureId = structureMap.get(structure.id);
        const newDeptEstimId = departmentEstimateMap.get(estimateLine.departmentEstimateId);
        const newEstimateLine = this.createEstimateLine(newStructureId, newDeptEstimId, estimateLine.categoryId);

        newEstimateLine.laborBillableFinal = estimateLine.laborBillableFinal;
        newEstimateLine.internalFeeBillableFinal = estimateLine.internalFeeBillableFinal;
        newEstimateLine.pcActivityId = estimateLine.pcActivityId;

        // copy departmentDetails
        if (estimateLine.departmentDetail) {
          const newDepartmentDetail = this.createDepartmentDetail(newEstimateLine.id);
          newDepartmentDetail.dateEnd = estimateLine.departmentDetail.dateEnd;
          newDepartmentDetail.dateStart = estimateLine.departmentDetail.dateStart;
          newDepartmentDetail.notes = estimateLine.departmentDetail.notes;
        }

        // copy categoryDetails
        if (estimateLine.categoryDetail) {
          const newCategoryDetail = this.createCategoryDetail(newEstimateLine.id);
          newCategoryDetail.dateEnd = estimateLine.categoryDetail.dateEnd;
          newCategoryDetail.dateStart = estimateLine.categoryDetail.dateStart;
          newCategoryDetail.notes = estimateLine.categoryDetail.notes;
        }

        // copy classificationLines
        for (const classificationLine of estimateLine.classificationLines) {
          const newClassificationEstimateId = classificationEstimateMap.get(
            classificationLine.classificationEstimateId
          );
          this.createClassificationLine(
            newEstimateLine.id,
            newClassificationEstimateId,
            classificationLine.hours,
            classificationLine.rateCost,
            classificationLine.rateBillable
          );
        }

        // copy internalFeeLines
        for (const internalFeeLine of estimateLine.internalFeeLines) {
          const newinternalFeeEstimateId = internalResourceEstimateMap.get(internalFeeLine.internalResourceEstimateId);
          this.createInternalFeeLine(
            newEstimateLine.id,
            newinternalFeeEstimateId,
            internalFeeLine.quantity,
            internalFeeLine.rateCost,
            internalFeeLine.rateBillable
          );
        }

        // copy expenseLines
        if (estimateLine.expenseLine) {
          this.createExpenseLine(newEstimateLine.id, estimateLine.expenseLine);
        }
      }
    }

    this.logger.info(`Created revision ('${newEstimate.revision}') '${newEstimate.id}' for Estimate '${estimate.id}'`);

    return newEstimate;
  }

  public deleteEstimate(estimateId: string): Observable<number> {
    const query = EntityQuery.from('DeleteEstimate').withParameters({ estimateId: estimateId });

    return from(
      this.executeQueryResult<number>(query).catch((error: Error) => {
        this.logger.error(`EstimateService.deleteEstimate(estimateId = '${estimateId}') : ${error.message}`);
        throw error;
      })
    );
  }

  /**
   * delete the classification-estimate that matches the given id
   * @param id
   */
  public async deleteClassificationEstimate(classificationEstimate: ClassificationEstimate) {
    const query = EntityQuery.from('DeleteClassificationEstimate').withParameters({ id: classificationEstimate.id });

    await this.executeQueryResults<DepartmentEstimate>(query).catch((error: Error) => {
      this.logger.error(
        `EstimateService.deleteClassificationEstimate(id = '${classificationEstimate.id}') : ${error.message}`
      );
      throw error;
    });

    await this.detachEntity(classificationEstimate);
  }

  /**
   * delete the internal-resource-estimate that matches the given id
   * @param id
   */
  public deleteInternalResourcesEstimate(id: string): Observable<InternalResource_Estimate[]> {
    const query = EntityQuery.from('DeleteInternalResourceEstimate').withParameters({ id: id });

    return from(
      this.executeQueryResults<InternalResource_Estimate>(query).catch((error: Error) => {
        this.logger.error(`EstimateService.deleteInternalResourcesEstimate(id = '${id}') : ${error.message}`);
        throw error;
      })
    );
  }

  /**
   * delete the department-estimate that matches the given id
   * @param id
   */
  public async deleteDepartmentEstimate(departmentEstimate: DepartmentEstimate) {
    const query = EntityQuery.from('DeleteDepartmentEstimate').withParameters({ id: departmentEstimate.id });

    await this.executeQueryResults<DepartmentEstimate>(query);

    // Breeze cannot perform cascade deletes, so instead of using Breeze setDeleted(),
    // we make a call to delete on the server, and once that is done we detach the entities from the Breeze cache
    await this.detachEntity(departmentEstimate);
  }

  /**
   * deletes all structures with the given level 1 id
   * @param estimateId
   * @param level1Id
   * @returns deleted structures
   */
  public deleteLevel1Structures(estimateId: string, level1Id: string): Observable<Structure[]> {
    const query = EntityQuery.from('DeleteLevel1Structures').withParameters({
      estimateId: estimateId,
      level1Id: level1Id
    });

    return from(
      this.executeQueryResults<Structure>(query).catch((error: Error) => {
        this.logger.error(
          `EstimateService.deleteLevel1Structures(estimateId = '${estimateId}', level1Id = '${level1Id}') : ${error.message}`
        );
        throw error;
      })
    );
  }

  /**
   *
   * @param id level 2 id
   * @returns deleted structures
   */
  public deleteLevel2Structure(id: string): Observable<Structure[]> {
    const query = EntityQuery.from('DeleteLevel2Structure').withParameters({ id: id });

    return from(this.executeQueryResults<Structure>(query));
  }

  /**
   *
   * @param id estimate line id
   * @returns deleted estimate line
   */
  public deleteEstimateLine(id: string): Observable<EstimateLine[]> {
    const query = EntityQuery.from('DeleteEstimateLine').withParameters({ id: id });

    return from(this.executeQueryResults<EstimateLine>(query));
  }

  public async deleteAllEstimateLines() {
    const estimateLines = this.currentEstimate.structures.flatMap(s => s.estimateLines);
    if (estimateLines.length === 0) {
      return;
    }

    estimateLines.forEach(line => {
      // Breeze doesnt support delete on cascade (https://stackoverflow.com/questions/26724082/cascade-delete-not-working-with-breeze)
      // Order is important
      line.expenseLine?.entityAspect.setDeleted();
      this.setDeletedArray(line.classificationLines);

      this.setDeletedArray(line.internalFeeLines);
      line.entityAspect.setDeleted();
    });

    await this.saveChangesAsync(true);

    this.currentEstimate$.next(this.currentEstimate);
  }

  private setDeletedArray(entities: Entity[]) {
    entities
      .slice() // slice is important here Breeze mutates the array after setting deleted
      .forEach(e => {
        e.entityAspect.setDeleted();
      });
  }

  /**
   *
   * @param departmentEstimateId
   * @returns query result (-1: no changes were made, >0: changes were made)
   */
  public async deleteEstimateLinesByDeptEstim(departmentEstimateId: string) {
    const query = EntityQuery.from('DeleteEstimateLinesByDeptEstim').withParameters({ departmentEstimateId });

    return await this.executeQueryResult<number>(query);
  }

  /**
   *
   * @param structureId
   * @param departmentEstimateId
   * @returns query output (error message if there is one)
   */
  public deleteEstimateLinesByStructByDeptEstim(structureId: string, departmentEstimateId: string) {
    const query = EntityQuery.from('DeleteEstimateLinesByStructByDeptEstim').withParameters({
      structureId: structureId,
      departmentEstimateId: departmentEstimateId
    });

    return this.executeQueryResult<string>(query);
  }

  /**
   * deletes the expense line with the given id
   * @param id expense line id
   * @returns deleted expense line
   */
  public deleteExpenseLine(id: string): Observable<ExpenseLine[]> {
    const query = EntityQuery.from('DeleteExpenseLine').withParameters({ id: id });

    return from(this.executeQueryResults<ExpenseLine>(query));
  }

  /**
   * deletes the classification line with the given id
   * and detaches it from the entity manager
   * @param id classification line id
   * @returns
   */
  public deleteClassificationLine(id: string): Observable<void> {
    const query = EntityQuery.from('DeleteClassificationLine').withParameters({ id: id });

    return from(
      this.executeQueryResults<ClassificationLine>(query)
        .then(classificationLineEntities => {
          if (!classificationLineEntities) {
            return;
          }

          return this.detachEntities(classificationLineEntities, false);
        })
        .then(() => this.logger.info(`EstimateService.deleteClassificationLine(id = '${id}') completed`))
    );
  }

  /**
   * deletes the internal fee line
   * and detaches it from the entity managager
   * @param id internal fee line id
   * @returns
   */
  public deleteInternalFeeLine(id: string): Observable<void> {
    const query = EntityQuery.from('DeleteInternalFeeLine').withParameters({ id: id });

    return from(
      this.executeQueryResults<InternalFeeLine>(query)
        .then(internalFeeLineEntities => {
          if (!internalFeeLineEntities) {
            return;
          }

          return this.detachEntities(internalFeeLineEntities, false);
        })
        .then(() => this.logger.info(`EstimateService.deleteInternalFeeLine(id = '${id}') completed`))
    );
  }

  /**
   * duplicates all structures with the given level1id
   * @param level1Id
   * @returns
   */
  public copyLevel1Structures(level1Id: string): Observable<string> {
    const query = EntityQuery.from('CopyLevel1Structures').withParameters({
      estimateId: this.currentEstimate.id,
      level1Id: level1Id
    });

    return from(
      this.executeQueryResult<string>(query).catch((error: Error) => {
        this.logger.error(
          `EstimateService.copyLevel1Structures(estimateId = '${this.currentEstimate.id}', level1Id = '${level1Id}') : ${error.message}`
        );
        throw error;
      })
    );
  }

  /**
   * creates a duplicate of the structure with the given level1 and level2 ids
   * relates the new structure to the target level1
   * @param level1Id
   * @param level2Id
   * @param targetLevel1Id
   * @returns
   */
  public copyLevel2Structures(level1Id: string, level2Id: string, targetLevel1Id: string): Observable<string[]> {
    const query = EntityQuery.from('CopyLevel2Structures').withParameters({
      estimateId: this.currentEstimate.id,
      level1Id: level1Id,
      level2Id: level2Id,
      targetLevel1Id: targetLevel1Id
    });

    return from(this.executeQueryResults<string>(query));
  }

  /**
   * compares certain fields of the current estimate with what is in the CRM
   * @returns an array of field names representing fields that need synchronizing
   */
  public getFieldsToSyncWithCrm(): Observable<string[]> {
    const query = EntityQuery.from('GetFieldsToSyncWithCrm').withParameters({ estimateId: this.currentEstimate.id });

    return from(this.executeQueryResults<string>(query));
  }

  public synchronizeEstimateWithCrm(): Observable<void> {
    const query = EntityQuery.from('SynchronizeEstimateWithCrm').withParameters({
      estimateId: this.currentEstimate.id
    });

    return from(
      this.executeQueryResults<Structure>(query)
        .then(structureEntities => {
          // structure entities are only returned if they have been deleted in the DB
          if (structureEntities) {
            // detach Structure entities from Breeze cache
            return this.detachEntities(structureEntities);
          }
        })
        .then(success => {
          if (isNil(success)) {
            return;
          }
          this.logger.info(`Detached structures after synchronizing project template with Crm : ${success.toString()}`);
        })
        .catch((error: Error) => {
          this.logger.error(
            `EstimateService.synchronizeEstimateWithCrm(estimateId = '${this.currentEstimate.id}') : ${error.message}`
          );
          throw error;
        })
    );
  }

  public async flushTreeTable() {
    await this.clear();
    this.getResourcesPromise = undefined;
    this.dialogEmitChanges();
    this.treetableEmitChanges();
  }

  private detachEntityFromEntityManager(entity: Entity) {
    const foundEntity = this.em.getEntityByKey(entity.entityAspect.getKey());
    return foundEntity && (this.em.detachEntity(foundEntity) as boolean);
  }

  public async detachEntity(entity: Entity, flushTreetable = true) {
    if (this.entityManagerHasChanges()) {
      await this.saveChangesAsync(); //everything needs to be saved before detaching from entityManager.
    }
    const success = this.detachEntityFromEntityManager(entity);

    if (flushTreetable) {
      await this.flushTreeTable();
    }

    return success;
  }

  public async detachEntities(entities: Entity[], flushTreetable = true) {
    if (this.entityManagerHasChanges()) {
      await this.saveChangesAsync(); //everything needs to be saved before detaching from entityManager.
    }

    let success = false;
    for (const entity of entities) {
      success = this.detachEntityFromEntityManager(entity) && success;
    }

    if (flushTreetable) {
      await this.flushTreeTable();
    }

    return success;
  }

  private async clear() {
    if (this.entityManagerHasChanges()) {
      await this.saveChangesAsync(); //everything needs to be saved before clearning entityManager.
    }
    this.resetCache();
    this.em.clear();
  }

  public async deployEstimate(isUsingPlanif: boolean) {
    const query = EntityQuery.from('DeployEstimate').withParameters({
      estimateId: this.currentEstimate.id,
      isUsingPlanif: isUsingPlanif
    });

    const results = await this.executeQueryResult<Estimate>(query);
    return results;
  }

  public getCustomers(): Observable<CustomerDto[]> {
    const query = EntityQuery.from('GetCustomers');

    return from(this.executeQueryResults<CustomerDto>(query));
  }

  public async getCompaniesAsync(): Promise<Company[]> {
    const query = EntityQuery.from('Companies');

    const results = await this.executeQueryResults<Company>(query);
    return results;
  }

  public getCustomerByCompany(companyCode: string): Observable<CustomerDto[]> {
    return from(this.getCustomerByCompanyAsync(companyCode));
  }

  public async getCustomerByCompanyAsync(companyCode: string) {
    const query = EntityQuery.from('GetCustomers').where('company', '==', companyCode);

    const results = await this.executeQueryResults<CustomerDto>(query);
    return results;
  }

  public async getDepartmentByProjectAsync(projectCode: string) {
    if (!projectCode) return undefined;

    const query = EntityQuery.from('GetDepartmentByProject').withParameters({ projectCode: projectCode });

    const result = await this.executeQueryResult<Department>(query);
    return result;
  }

  public async retrieveExistingStructures(): Promise<Estimate> {
    const query = EntityQuery.from('RetrieveExistingStructures').withParameters({
      estimateId: this.currentEstimate.id
    });

    return this.executeQueryResult<Estimate>(query);
  }

  public async getAlreadyDeployedSubLevels(): Promise<ProblematicSubLevelsDto> {
    const query = EntityQuery.from('GetAlreadyDeployedSubLevels').withParameters({
      estimateId: this.currentEstimate.id
    });

    const problematicSubLevels = await this.executeQueryResult<ProblematicSubLevelsDto>(query);

    this.alreadyDeployedSubLevels$.next(problematicSubLevels);
    return problematicSubLevels;
  }

  public observeAlreadyDeployedSubLevels(): Observable<ProblematicSubLevelsDto | undefined> {
    return this.alreadyDeployedSubLevels$;
  }

  public getServiceTypes(): Observable<ServiceTypeDto[]> {
    const query = EntityQuery.from('ServiceTypes');

    return from(this.executeQueryResults<ServiceTypeDto>(query));
  }
}
